import React, { useState } from "react";
import { createMagicLink, describeScope, type MagicLinkScope } from "../../admin/magicLink.js";

interface Props {
  scope: MagicLinkScope;
  onClose: () => void;
}

/**
 * The one magic-link dialog, shared by every entry point (folder right-click,
 * single-file modal, selection bar). Creates the link server-side and shows it
 * with a copy button. Signed-in curators only — App gates the openers.
 */
export function MagicLinkModal({ scope, onClose }: Props) {
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const { url } = await createMagicLink(scope, label.trim());
      setResult(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard denied — the URL is selectable below */
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal magic-link-modal">
        <header>
          <h2>{result ? "Magic link created" : "Create magic link"}</h2>
          <button className="close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="modal-body">
          {!result && (
            <>
              <p className="magic-link-desc">
                This creates a private link to {describeScope(scope)}. Anyone who has the
                link can view and download exactly those images — nothing else — without
                signing in. Treat the link like a key: fine to send to a customer or
                distributor, but it shouldn&rsquo;t be posted anywhere public. It can be
                revoked at any time.
              </p>
              <label className="magic-link-label">
                Label (who / what it&rsquo;s for — shown on the shared page)
                <input
                  type="text"
                  value={label}
                  maxLength={200}
                  placeholder="e.g. Acme Safety — respirator imagery"
                  onChange={(e) => setLabel(e.target.value)}
                />
              </label>
              {error && <p className="admin-commit-error" role="alert">{error}</p>}
            </>
          )}
          {result && (
            <>
              <p className="magic-link-desc">Send this link to the recipient:</p>
              <div className="magic-link-result">
                <input type="text" readOnly value={result} onFocus={(e) => e.target.select()} />
                <button className="magic-link-copy" onClick={copy}>
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
              <p className="magic-link-hint">
                To revoke it later, ask IT (links can be disabled instantly).
              </p>
            </>
          )}
        </div>
        <footer className="admin-commit-footer">
          {!result ? (
            <>
              <button className="admin-commit-cancel" onClick={onClose}>Cancel</button>
              <button className="admin-commit-go" onClick={create} disabled={creating}>
                {creating ? "Creating…" : "Create link"}
              </button>
            </>
          ) : (
            <button className="admin-commit-go" onClick={onClose}>Done</button>
          )}
        </footer>
      </div>
    </div>
  );
}
